"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronRight, ArrowRight } from "lucide-react";

interface AttentionItem {
  type: "manual" | "drinks_no_ficha" | "cozinha_no_ficha";
  count: number;
  link: string;
  title: string;
}

interface AttentionItemsProps {
  items: AttentionItem[];
  loading: boolean;
}

type Priority = "warn" | "ok" | "neutral";

interface ItemMeta {
  priority: Priority;
  subtitle: string;
  detail: string;
  action: string;
}

const ITEM_META: Record<AttentionItem["type"], ItemMeta> = {
  manual: {
    priority: "ok",
    subtitle: "artigos aguardando leitura",
    detail: "Leia os artigos atualizados do manual operacional para manter a equipe alinhada.",
    action: "Ver artigos",
  },
  drinks_no_ficha: {
    priority: "warn",
    subtitle: "drinks sem ficha · afeta controle de custo",
    detail: "Atualize as fichas técnicas para manter o controle de custos e margem real dos produtos.",
    action: "Atualizar fichas",
  },
  cozinha_no_ficha: {
    priority: "warn",
    subtitle: "pratos sem ficha · afeta controle de custo",
    detail: "Atualize as fichas técnicas para manter o controle de custos e margem real dos produtos.",
    action: "Atualizar fichas",
  },
};

const PIP_CLASSES: Record<Priority, string> = {
  warn: "bg-[#ff6b2b]",
  ok: "bg-[#3ed97a]",
  neutral: "bg-muted-foreground/40",
};

const COUNT_CLASSES: Record<Priority, string> = {
  warn: "bg-[#ff6b2b] text-white",
  ok: "bg-[#3ed97a] text-black",
  neutral: "bg-muted text-muted-foreground",
};

const ACTION_CLASSES: Record<Priority, string> = {
  warn: "bg-[#ff6b2b] text-white",
  ok: "bg-[#3ed97a] text-black",
  neutral: "bg-secondary text-muted-foreground",
};

export function AttentionItems({ items, loading }: AttentionItemsProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (loading || items.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {items.map((item, i) => {
        const key = `${item.type}-${i}`;
        const meta = ITEM_META[item.type];
        const isOpen = openKey === key;
        const isLast = i === items.length - 1;

        return (
          <div
            key={key}
            className={!isLast ? "border-b border-border" : undefined}
          >
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : key)}
              className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-secondary/40"
            >
              <span
                className={`w-[3px] self-stretch min-h-[20px] rounded-sm ${PIP_CLASSES[meta.priority]}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-13 font-semibold text-foreground">
                    {item.title}
                  </p>
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-px text-2xs font-bold ${COUNT_CLASSES[meta.priority]}`}
                  >
                    {item.count}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-11 text-muted-foreground/70">
                  {item.count} {meta.subtitle}
                </p>
              </div>
              <ChevronRight
                className={`size-3 shrink-0 text-muted-foreground/70 transition-transform ${
                  isOpen ? "rotate-90" : ""
                }`}
              />
            </button>

            {isOpen && (
              <div className="border-t border-border px-3.5 pt-2.5 pb-3.5 pl-[29px]">
                <p className="mb-2.5 text-xs leading-relaxed text-muted-foreground">
                  {meta.detail}
                </p>
                <Link
                  href={item.link}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-opacity hover:opacity-80 ${ACTION_CLASSES[meta.priority]}`}
                >
                  {meta.action}
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
